import { useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { identityApi } from '../services/api';
import { useAuth } from '../store/auth-context';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import type { PlatformEvent } from '../types/events';
import type {
  ApiKeyStoreRequest,
  AwsRoleSimulation,
  AwsRoleStoreRequest,
  AwsRoleStoreResponse,
  Credential,
  CredentialListResponse,
  OAuthManualStoreRequest,
  OAuthStartRequest,
  OAuthStartResponse,
  OrgAppCredentialRequest,
  OrgServiceAccountRequest,
} from '../types/identity';

const KEY_ROOT = ['identity'] as const;

const userKey = (): QueryKey => [...KEY_ROOT, 'user'];
const spaceKey = (spaceId: string): QueryKey => [...KEY_ROOT, 'space', spaceId];
const orgKey = (): QueryKey => [...KEY_ROOT, 'org'];
const providerCatalogKey = (): QueryKey => [...KEY_ROOT, 'providers'];

const MCP_CONNECTION_SOURCE = 'optr.mcp.connections';

/**
 * Bus subscription shared by every scope hook. The list endpoints are
 * disjoint by scope, but the same WebSocket signals (MCP connection
 * completed, space membership change) can affect any of them — invalidate
 * the whole `identity` subtree on those.
 */
function useIdentityInvalidationBus() {
  const { isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const { subscribe } = usePlatformEvents();

  useEffect(() => {
    if (!isAuthenticated) return;
    const unsub = subscribe([{}], (event: PlatformEvent) => {
      if (
        event.source === MCP_CONNECTION_SOURCE ||
        event.eventType?.startsWith('optr.space.') ||
        event.eventType?.startsWith('optr.identity.')
      ) {
        qc.invalidateQueries({ queryKey: KEY_ROOT });
      }
    });
    return unsub;
  }, [isAuthenticated, subscribe, qc]);
}

/**
 * User-scope credentials — the caller's own creds. Powers the Personal
 * tab; one query per logged-in caller (no path parameter because the
 * user is resolved from the JWT).
 */
export function useMyCredentials() {
  const { isAuthenticated } = useAuth();
  useIdentityInvalidationBus();
  return useQuery<CredentialListResponse>({
    queryKey: userKey(),
    queryFn: () => identityApi.me.credentials(),
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  });
}

/** Space-scope credentials. One query per spaceId — cache keys are tab-local. */
export function useSpaceCredentials(spaceId: string | undefined) {
  const { isAuthenticated } = useAuth();
  useIdentityInvalidationBus();
  return useQuery<CredentialListResponse>({
    queryKey: spaceKey(spaceId ?? '__none__'),
    queryFn: () => identityApi.space(spaceId!).credentials(),
    enabled: isAuthenticated && !!spaceId,
    staleTime: 60 * 1000,
  });
}

/**
 * Org-scope credentials. Only enabled for org admins — the backend would
 * 403/404 non-admins anyway, but skipping the request keeps the network
 * panel quiet and avoids the global auth-failure side-effect on 403.
 */
export function useOrgCredentials() {
  const { isAuthenticated, user } = useAuth();
  useIdentityInvalidationBus();
  const isOrgAdmin = user?.role === 'admin';
  return useQuery<CredentialListResponse>({
    queryKey: orgKey(),
    queryFn: () => identityApi.org.credentials(),
    enabled: isAuthenticated && isOrgAdmin,
    staleTime: 60 * 1000,
  });
}

/**
 * Provider catalog. Org-agnostic and changes on the order of weeks — cache
 * for an hour so re-mounts of the credentials page don't refetch it.
 */
export function useProviderCatalog() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: providerCatalogKey(),
    queryFn: () => identityApi.providers(),
    enabled: isAuthenticated,
    staleTime: 60 * 60 * 1000,
  });
}

/** Generic invalidator — use after a mutation completes. */
export function useInvalidateIdentity() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: KEY_ROOT });
}

// ----------------------------------------------------------------
// Mutations. Each invalidates the whole identity subtree on success;
// the per-scope queries are cheap (~one round-trip each) and this keeps
// cache reasoning simple in the face of cross-scope effects (e.g.,
// `set-default` may promote a sibling that lives in a different group).
// ----------------------------------------------------------------

export function useVerifyCredential() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (credId: string) => identityApi.credential(credId).verify(),
    onSuccess: () => invalidate(),
  });
}

/** Simulated AssumeRole test for an `aws-role` credential ("Test as agent…"). */
export function useVerifyAwsRole() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: ({ credId, simulation }: { credId: string; simulation: AwsRoleSimulation }) =>
      identityApi.credential(credId).verify(simulation),
    onSuccess: () => invalidate(),
  });
}

export function useRevokeCredential() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (credId: string) => identityApi.credential(credId).revoke(),
    onSuccess: () => invalidate(),
  });
}

export function useSetDefaultCredential() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (credId: string) => identityApi.credential(credId).setDefault(),
    onSuccess: () => invalidate(),
  });
}

export function useRenameCredential() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: ({ credId, accountName }: { credId: string; accountName: string }) =>
      identityApi.credential(credId).rename(accountName),
    onSuccess: () => invalidate(),
  });
}

type StartOAuthVars = {
  scope: 'user' | { spaceId: string };
  provider: string;
  body: Omit<OAuthStartRequest, 'provider'>;
};

/**
 * Start an OAuth flow. The mutation returns `authorizationUrl` to open
 * in a new tab; cache invalidation happens later via the
 * `optr.mcp.connections` (or `optr.identity.*`) WebSocket event when
 * the round-trip completes — not on this mutation's success.
 *
 * `scope === 'user'` routes to the top-level
 * `POST /oauth/{provider}/start`; `scope === { spaceId }` routes to the
 * space endpoint. There is no org-level OAuth start, so org callers
 * use `useStoreOAuthManual` (paste-token) instead.
 */
export function useStartOAuth() {
  return useMutation<OAuthStartResponse, Error, StartOAuthVars>({
    mutationFn: ({ scope, provider, body }) =>
      scope === 'user'
        ? identityApi.me.oauthStart({ provider, ...body })
        : identityApi.space(scope.spaceId).oauthStart({ provider, ...body }),
  });
}

type StoreApiKeyVars =
  | { scope: 'user'; body: ApiKeyStoreRequest }
  | { scope: { spaceId: string }; body: ApiKeyStoreRequest }
  | { scope: 'org'; body: OrgServiceAccountRequest };

export function useStoreApiKey() {
  const invalidate = useInvalidateIdentity();
  return useMutation<{ credential: Credential }, Error, StoreApiKeyVars>({
    mutationFn: (vars) => {
      if (vars.scope === 'user') return identityApi.me.apiKey(vars.body);
      if (vars.scope === 'org') return identityApi.org.serviceAccount(vars.body);
      return identityApi.space(vars.scope.spaceId).apiKey(vars.body);
    },
    onSuccess: () => invalidate(),
  });
}

/**
 * Register an org app/bot service-account credential (e.g. the Operator Google
 * Chat app SA key). Admin-only; maps to `PUT /api/v1/org/credentials/app`.
 */
export function useStoreAppCredential() {
  const invalidate = useInvalidateIdentity();
  return useMutation<{ credential: Credential }, Error, OrgAppCredentialRequest>({
    mutationFn: (body) => identityApi.org.appCredential(body),
    onSuccess: () => invalidate(),
  });
}

type StoreOAuthManualVars =
  | { scope: 'user'; body: OAuthManualStoreRequest }
  | { scope: { spaceId: string }; body: OAuthManualStoreRequest }
  | { scope: 'org'; body: OAuthManualStoreRequest };

/**
 * Store a pasted OAuth token bundle. Identity force-rotates the refresh
 * token on submit, so the user's local copy stops working — surface that in
 * the calling modal. HTTP 422 indicates the refresh failed (token bad or
 * already used); other errors are generic.
 */
export function useStoreOAuthManual() {
  const invalidate = useInvalidateIdentity();
  return useMutation<{ credential: Credential }, Error, StoreOAuthManualVars>({
    mutationFn: (vars) => {
      if (vars.scope === 'user') return identityApi.me.oauthManual(vars.body);
      if (vars.scope === 'org') return identityApi.org.oauthManual(vars.body);
      return identityApi.space(vars.scope.spaceId).oauthManual(vars.body);
    },
    onSuccess: () => invalidate(),
  });
}

type StoreAwsRoleVars =
  | { scope: { spaceId: string }; body: AwsRoleStoreRequest }
  | { scope: 'org'; body: AwsRoleStoreRequest };

/**
 * Register an AWS IAM role on a shared space or the org. Resolves (not
 * rejects) when the simulated AssumeRole fails — the role is saved with
 * `status: 'error'` and `test.ok === false` carries the AWS message.
 */
export function useStoreAwsRole() {
  const invalidate = useInvalidateIdentity();
  return useMutation<AwsRoleStoreResponse, Error, StoreAwsRoleVars>({
    mutationFn: (vars) =>
      vars.scope === 'org'
        ? identityApi.org.awsRole(vars.body)
        : identityApi.space(vars.scope.spaceId).awsRole(vars.body),
    onSuccess: () => invalidate(),
  });
}

/** Server-rendered trust policy for the AWS-role policy builder. */
export function useAwsRoleTrustPolicy(opts: { spaceId?: string; agentIds: string[]; enabled: boolean }) {
  return useQuery({
    queryKey: [...KEY_ROOT, 'aws-role-trust-policy', opts.spaceId ?? null, [...opts.agentIds].sort()],
    queryFn: () =>
      identityApi.awsRoleTrustPolicy({ spaceId: opts.spaceId, agentIds: opts.agentIds }),
    enabled: opts.enabled,
    staleTime: 5 * 60 * 1000,
  });
}
